import { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { BASE_URL } from "../Constant";

// Default = The Diamond Counter. White-label retailers override the logo +
// name (see the "Front end" tab on the admin retailer page).
const DEFAULTS = {
  whitelabel: false,
  name: "The Diamond Counter",
  logo: "/logo.png",
  footerLogo: "/footer-logo.png",
};

// Build a branding object from a /user/my-settings payload (or its cached copy).
export function computeBranding(settings) {
  if (!settings || !settings.Whitelabel_Enabled) return DEFAULTS;
  const name = (settings.Branding_Company_Name || "").trim() || DEFAULTS.name;
  const logoPath = settings.Branding_Logo && settings.Branding_Logo.path;
  const logo = logoPath ? `${BASE_URL}${logoPath}` : DEFAULTS.logo;
  // White-label uses the single uploaded logo for both header and footer.
  return { whitelabel: true, name, logo, footerLogo: logo };
}

// Synchronous read from the cache the Header writes after /user/my-settings.
// Safe to call anywhere — including components that render before the provider
// has fetched (e.g. the splash loader). Falls back to TDC defaults.
export function getBrandingFromCache() {
  try {
    const raw = localStorage.getItem("tdc_retailer_settings");
    if (!raw) return DEFAULTS;
    return computeBranding(JSON.parse(raw));
  } catch {
    return DEFAULTS;
  }
}

const BrandingContext = createContext(DEFAULTS);

export function BrandingProvider({ children }) {
  const token = useSelector((state) => state.user?.token);
  const [branding, setBranding] = useState(() => getBrandingFromCache());

  useEffect(() => {
    if (!token) {
      setBranding(DEFAULTS);
      return;
    }
    let cancelled = false;
    // Instant paint from cache, then refresh authoritative branding.
    setBranding(getBrandingFromCache());
    axios
      .get(`${BASE_URL}/user/my-settings`, { headers: { Authorization: token } })
      .then((res) => {
        const settings = res?.data?.settings;
        if (!settings || cancelled) return;
        try {
          localStorage.setItem("tdc_retailer_settings", JSON.stringify(settings));
        } catch {}
        setBranding(computeBranding(settings));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Reflect branding in the browser tab (title + favicons) when white-labelled,
  // so a customer shown the screen never sees "The Diamond Counter". Restored
  // on logout / when the flag is off.
  useEffect(() => {
    if (!branding.whitelabel) return;
    const prevTitle = document.title;
    document.title = branding.name;
    const icons = document.querySelectorAll(
      "link[rel~='icon'], link[rel='apple-touch-icon']"
    );
    const prev = [];
    icons.forEach((l) => {
      prev.push([l, l.getAttribute("href")]);
      l.setAttribute("href", branding.logo);
    });
    return () => {
      document.title = prevTitle;
      prev.forEach(([l, href]) => {
        if (href != null) l.setAttribute("href", href);
      });
    };
  }, [branding.whitelabel, branding.name, branding.logo]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
